import type { AnyPalette, PaletteLibrary } from './types';

export type LibraryFilter = 'all' | 'system' | 'personal' | 'workspace' | 'favorites';

/** Every palette the viewer can pick from, system first, then personal, then workspace ones. */
export function allPalettes(library: PaletteLibrary): AnyPalette[] {
  return [...library.system, ...library.personal, ...library.workspace];
}

const HEX = /^#?[0-9a-f]{1,6}$/i;

/**
 * Palettes matching a filter and a text. The text matches the palette name (and the workspace
 * name of a shared one) case-insensitively and in part, every word; a hex-like text (`#ff`,
 * `a1b2`) also matches the colors the palette contains. `nameOf` gives the localized name of a
 * system palette.
 */
export function filterPalettes(
  library: PaletteLibrary,
  {
    filter,
    query,
    nameOf,
  }: { filter: LibraryFilter; query: string; nameOf: (id: string) => string },
): AnyPalette[] {
  const favorites = new Set(library.favorite_palettes);
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return allPalettes(library).filter((palette) => {
    if (
      filter === 'favorites'
        ? !favorites.has(palette.id)
        : filter !== 'all' && palette.scope !== filter
    ) {
      return false;
    }
    if (words.length === 0) return true;
    const user = palette.scope === 'system' ? null : palette;
    const haystack = [
      palette.scope === 'system' ? nameOf(palette.id) : user?.name,
      user?.workspace_name,
    ]
      .filter(Boolean)
      .join('\n')
      .toLocaleLowerCase();
    return words.every(
      (word) =>
        haystack.includes(word) ||
        (HEX.test(word) &&
          palette.colors.some((color) => color.includes(word.replace('#', '').toLowerCase()))),
    );
  });
}
